import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { type RetailerLabelType, retailerLabels } from "../utils/labels";
import type { BooleanStringType } from "../../../../common-deprecated/types/CommonTypes";

type AEMRetailerSettingsType = {
    hideDirectMonthlyTotal: BooleanStringType; // When enabled, the monthly totals will never be shown directly. This makes it so that the monthly rates are ony accessible by reading the finance disclaimers. This is a temporary setting (See DR-1218).
};

export type AemRetailerScreenSettingsType = {
    settings: Partial<AEMRetailerSettingsType>;
    labels: Partial<Record<RetailerLabelType, string>>;
    config: NonNullable<unknown>; // config is not used, so an actual empty object
};

// Mirrors typing found in dxp-screens project
export type NavBoxType = {
    parentHeight: string;
    leftOffset: string;
    rightOffset: string;
    flipNavButtons: boolean;
};

export type RetailerSettingsType = {
    initialized: boolean;
    isRetailerVariant: boolean; // ?retailerscreen=true
    enableSelectRetailer: boolean; // USC only, ?enableSelectRetailer=true
    navBox: NavBoxType | null; // ?retailerscreenNavBox=height,left,right,flipNavButtons
    disableExternalQrModal: boolean; // ?disableExternalQrModal=true
    hideDirectMonthlyTotal: boolean;
    labels: Record<RetailerLabelType, string>;
};

export const initialStateRetailerSettings: RetailerSettingsType = {
    initialized: false,
    isRetailerVariant: false,
    enableSelectRetailer: false,
    navBox: { parentHeight: "154px", leftOffset: "3px", rightOffset: "3px", flipNavButtons: false },
    disableExternalQrModal: false,
    hideDirectMonthlyTotal: false,
    // In comparison with the other settings slices, we don't set the default labels here,
    // but instead during init, this limits the filesize impact on non retailer variants.
    labels: {} as typeof retailerLabels,
};

/**
 * This slice should only be initialized when loading the retailer variant of a component (?retailerscreen=true)
 */
const RetailerSettingsSlice = createSlice({
    name: "retailerSettings",
    initialState: initialStateRetailerSettings,
    reducers: {
        initRetailerSettings(state, action: PayloadAction<RetailerSettingsType>) {
            return {
                ...state,
                ...action.payload,
                labels: {
                    ...retailerLabels,
                    ...action.payload.labels,
                },
                initialized: true,
            };
        },
        updateNavBox(state, action: PayloadAction<NavBoxType>) {
            return { ...state, navBox: action.payload };
        },
    },
});

export type RetailerSettingsActionsType = ReturnType<typeof initRetailerSettings> | ReturnType<typeof updateNavBox>;
export const { initRetailerSettings, updateNavBox } = RetailerSettingsSlice.actions;

export default RetailerSettingsSlice;
